import { z } from 'zod';
import {
  BOARD_MEMBER_ROLES,
  HEX_COLOR_REGEX,
  MY_WORK_FILTERS,
  TASK_PRIORITIES,
  type BoardAccessRelation,
  type BoardMemberRole,
  type MyWorkFilter,
  type TaskPriority,
} from '../constants/tasks.js';

// -------------------- Enums --------------------

export const taskPrioritySchema = z.enum(TASK_PRIORITIES);
export const boardMemberRoleSchema = z.enum(BOARD_MEMBER_ROLES);
export const myWorkFilterSchema = z.enum(MY_WORK_FILTERS);

const hexColor = z
  .string()
  .trim()
  .regex(HEX_COLOR_REGEX, 'Colour must be a hex value like #1d4ed8');

const taskTitle = z
  .string()
  .trim()
  .min(1, 'Title is required')
  .max(280, 'Title must be 280 characters or fewer');

/** Optional YYYY-MM-DD due date; null clears it. */
const dueDateField = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format')
  .nullable();

// -------------------- Move / ordering --------------------

/**
 * Fractional-rank move contract. The server places the moved row at the
 * midpoint between `afterId`'s and `beforeId`'s positions. Omitting both
 * appends to the end of the list; `afterId: null` means "make it first".
 */
export const positionMoveSchema = z.object({
  afterId: z.string().uuid().nullable().optional(),
  beforeId: z.string().uuid().nullable().optional(),
});
export type PositionMoveRequest = z.infer<typeof positionMoveSchema>;

// -------------------- Board --------------------

export const createBoardRequestSchema = z.object({
  name: z.string().trim().min(1, 'Board name is required').max(120, 'Name is too long'),
  description: z.string().trim().max(2000, 'Description is too long').optional(),
});
export type CreateBoardRequest = z.infer<typeof createBoardRequestSchema>;

export const updateBoardRequestSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'Board name is required')
      .max(120, 'Name is too long')
      .optional(),
    description: z.string().trim().max(2000, 'Description is too long').nullable().optional(),
  })
  .refine((v) => v.name !== undefined || v.description !== undefined, 'Nothing to update.');
export type UpdateBoardRequest = z.infer<typeof updateBoardRequestSchema>;

// -------------------- Board members --------------------

export const addBoardMemberRequestSchema = z.object({
  userId: z.string().uuid('Select a person to add.'),
  role: boardMemberRoleSchema.default('MEMBER'),
});
export type AddBoardMemberRequest = z.infer<typeof addBoardMemberRequestSchema>;

// -------------------- Board statuses --------------------

export const createBoardStatusRequestSchema = z.object({
  name: z.string().trim().min(1, 'Status name is required').max(40, 'Name is too long'),
  color: hexColor,
  isDone: z.boolean().default(false),
});
export type CreateBoardStatusRequest = z.infer<typeof createBoardStatusRequestSchema>;

export const updateBoardStatusRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(40).optional(),
    color: hexColor.optional(),
    isDone: z.boolean().optional(),
  })
  .refine(
    (v) => v.name !== undefined || v.color !== undefined || v.isDone !== undefined,
    'Nothing to update.',
  );
export type UpdateBoardStatusRequest = z.infer<typeof updateBoardStatusRequestSchema>;

export const reorderBoardStatusesRequestSchema = z.object({
  statusIds: z.array(z.string().uuid()).min(1, 'Provide the new status order.'),
});
export type ReorderBoardStatusesRequest = z.infer<typeof reorderBoardStatusesRequestSchema>;

/** Deleting an in-use status requires reassigning its tasks to another status. */
export const deleteBoardStatusQuerySchema = z.object({
  reassignTo: z.string().uuid().optional(),
});
export type DeleteBoardStatusQuery = z.infer<typeof deleteBoardStatusQuerySchema>;

// -------------------- Groups --------------------

export const createGroupRequestSchema = z.object({
  name: z.string().trim().min(1, 'Group name is required').max(120, 'Name is too long'),
});
export type CreateGroupRequest = z.infer<typeof createGroupRequestSchema>;

export const updateGroupRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    isArchived: z.boolean().optional(),
  })
  .refine((v) => v.name !== undefined || v.isArchived !== undefined, 'Nothing to update.');
export type UpdateGroupRequest = z.infer<typeof updateGroupRequestSchema>;

// -------------------- Labels --------------------

export const createLabelRequestSchema = z.object({
  name: z.string().trim().min(1, 'Label name is required').max(40, 'Name is too long'),
  color: hexColor,
});
export type CreateLabelRequest = z.infer<typeof createLabelRequestSchema>;

export const updateLabelRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(40).optional(),
    color: hexColor.optional(),
  })
  .refine((v) => v.name !== undefined || v.color !== undefined, 'Nothing to update.');
export type UpdateLabelRequest = z.infer<typeof updateLabelRequestSchema>;

// -------------------- Tasks --------------------

export const createTaskRequestSchema = z.object({
  groupId: z.string().uuid('Select a group for this task.'),
  title: taskTitle,
  description: z.string().trim().max(5000, 'Description is too long').optional(),
  priority: taskPrioritySchema.default('NORMAL'),
  statusId: z.string().uuid().optional(),
  dueDate: dueDateField.optional(),
  assigneeIds: z.array(z.string().uuid()).max(20).optional(),
  labelIds: z.array(z.string().uuid()).max(20).optional(),
});
export type CreateTaskRequest = z.infer<typeof createTaskRequestSchema>;

export const updateTaskRequestSchema = z
  .object({
    title: taskTitle.optional(),
    description: z.string().trim().max(5000, 'Description is too long').nullable().optional(),
    priority: taskPrioritySchema.optional(),
    dueDate: dueDateField.optional(),
  })
  .refine(
    (v) =>
      v.title !== undefined ||
      v.description !== undefined ||
      v.priority !== undefined ||
      v.dueDate !== undefined,
    'Nothing to update.',
  );
export type UpdateTaskRequest = z.infer<typeof updateTaskRequestSchema>;

/** Move a task within/across Kanban cells. groupId+statusId target the cell. */
export const moveTaskRequestSchema = z.object({
  groupId: z.string().uuid().optional(),
  statusId: z.string().uuid().optional(),
  afterId: z.string().uuid().nullable().optional(),
  beforeId: z.string().uuid().nullable().optional(),
});
export type MoveTaskRequest = z.infer<typeof moveTaskRequestSchema>;

export const changeTaskStatusRequestSchema = z.object({
  statusId: z.string().uuid('Select a status.'),
});
export type ChangeTaskStatusRequest = z.infer<typeof changeTaskStatusRequestSchema>;

export const addAssigneeRequestSchema = z.object({
  userId: z.string().uuid('Select a person to assign.'),
});
export type AddAssigneeRequest = z.infer<typeof addAssigneeRequestSchema>;

export const setTaskLabelsRequestSchema = z.object({
  labelIds: z.array(z.string().uuid()).max(20),
});
export type SetTaskLabelsRequest = z.infer<typeof setTaskLabelsRequestSchema>;

// -------------------- Checklist --------------------

export const createChecklistItemRequestSchema = z.object({
  text: z.string().trim().min(1, 'Checklist item is required').max(280, 'Text is too long'),
});
export type CreateChecklistItemRequest = z.infer<typeof createChecklistItemRequestSchema>;

export const updateChecklistItemRequestSchema = z
  .object({
    text: z.string().trim().min(1).max(280).optional(),
    isComplete: z.boolean().optional(),
  })
  .refine((v) => v.text !== undefined || v.isComplete !== undefined, 'Nothing to update.');
export type UpdateChecklistItemRequest = z.infer<typeof updateChecklistItemRequestSchema>;

// -------------------- Comments --------------------

export const createTaskCommentRequestSchema = z.object({
  body: z.string().trim().min(1, 'Comment is required').max(4000, 'Comment is too long'),
  mentionedUserIds: z.array(z.string().uuid()).max(30).optional(),
});
export type CreateTaskCommentRequest = z.infer<typeof createTaskCommentRequestSchema>;

export const editTaskCommentRequestSchema = z.object({
  body: z.string().trim().min(1, 'Comment is required').max(4000, 'Comment is too long'),
});
export type EditTaskCommentRequest = z.infer<typeof editTaskCommentRequestSchema>;

export const listTaskCommentsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListTaskCommentsQuery = z.infer<typeof listTaskCommentsQuerySchema>;

// -------------------- My Work --------------------

export const myWorkQuerySchema = z.object({
  filter: myWorkFilterSchema.default('open'),
  // CO-only: inspect a specific officer's queue. Ignored for non-CO callers.
  userId: z.string().uuid().optional(),
});
export type MyWorkQuery = z.infer<typeof myWorkQuerySchema>;

// -------------------- Response DTOs (ISO date strings) --------------------

export interface BoardMemberDto {
  userId: string;
  name: string;
  rank: string | null;
  baNumber: string | null;
  role: BoardMemberRole;
  avatarColor: string | null;
}

export interface BoardStatusDto {
  id: string;
  name: string;
  color: string;
  position: number;
  isDone: boolean;
}

export interface BoardGroupDto {
  id: string;
  name: string;
  position: number;
  isArchived: boolean;
}

export interface LabelDto {
  id: string;
  name: string;
  color: string;
}

export interface ChecklistItemDto {
  id: string;
  text: string;
  isComplete: boolean;
  position: number;
}

export interface TaskAssigneeDto {
  userId: string;
  name: string;
  rank: string | null;
  avatarColor: string | null;
  /** Battalion appointment held by this officer ('2IC' | 'ADJT' | 'QM' | null). */
  appointment: string | null;
}

export interface TaskCommentDto {
  id: string;
  taskId: string;
  authorId: string;
  authorName: string;
  authorAvatarColor: string | null;
  body: string;
  mentionedUserIds: string[];
  editedAt: string | null;
  createdAt: string;
}

export interface TaskActivityDto {
  id: string;
  type: string;
  detail: Record<string, unknown> | null;
  actorId: string | null;
  actorName: string | null;
  createdAt: string;
}

export interface TaskDto {
  id: string;
  boardId: string;
  groupId: string;
  statusId: string;
  title: string;
  description: string | null;
  priority: TaskPriority;
  dueDate: string | null;
  position: number;
  isArchived: boolean;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  assignees: TaskAssigneeDto[];
  labels: LabelDto[];
  checklistTotal: number;
  checklistDoneCount: number;
  commentCount: number;
}

export interface TaskDetailDto extends TaskDto {
  checklist: ChecklistItemDto[];
  activity: TaskActivityDto[];
}

export interface BoardSummaryDto {
  id: string;
  name: string;
  description: string | null;
  ownerId: string;
  ownerName: string;
  isArchived: boolean;
  relation: BoardAccessRelation;
  memberCount: number;
  taskCount: number;
  openTaskCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface BoardSnapshotDto {
  board: BoardSummaryDto;
  members: BoardMemberDto[];
  statuses: BoardStatusDto[];
  groups: BoardGroupDto[];
  labels: LabelDto[];
  tasks: TaskDto[];
}

/** A My Work row carries enough board/status context to render standalone. */
export interface MyWorkTaskDto extends TaskDto {
  boardName: string;
  statusName: string;
  statusColor: string;
  statusIsDone: boolean;
}

export interface MyWorkResponse {
  filter: MyWorkFilter;
  tasks: MyWorkTaskDto[];
}

// -------------------- CO overview DTOs --------------------

export interface OverviewStatusCountDto {
  statusName: string;
  color: string;
  count: number;
}

export interface OverviewBoardDto {
  boardId: string;
  name: string;
  ownerName: string;
  total: number;
  done: number;
  overdue: number;
  byStatus: OverviewStatusCountDto[];
}

export interface WorkloadRowDto {
  userId: string;
  name: string;
  rank: string | null;
  avatarColor: string | null;
  open: number;
  overdue: number;
  done: number;
}

/** Per-person workload summary for the People directory. */
export interface PersonWorkloadDto {
  open: number;
  overdue: number;
  /** Tasks closed within the last 30 days. */
  done30d: number;
}

/** A board the user owns or is a member of, surfaced as a People-page chip. */
export interface PersonBoardDto {
  boardId: string;
  name: string;
  /** OWNER or MEMBER. CO ownership is handled via role override, not this list. */
  role: string;
}

/** A row on the People page — directory entry + workload + boards. */
export interface PersonDetailsDto {
  userId: string;
  name: string;
  rank: string | null;
  role: string;
  baNumber: string | null;
  avatarColor: string | null;
  /** Battalion appointment (2IC | ADJT | QM | null). Renders as a chip next to the name. */
  appointment: string | null;
  workload: PersonWorkloadDto;
  boards: PersonBoardDto[];
}

// -------------------- Task Update Requests (Phase 5) --------------------

/**
 * Three statuses an officer can attach to a posted update. Optional — the
 * composer also accepts a freeform body with no status.
 */
export const TASK_UPDATE_STATUSES = ['ON_TRACK', 'BLOCKED', 'NEED_HELP'] as const;
export type TaskUpdateStatus = (typeof TASK_UPDATE_STATUSES)[number];
export const taskUpdateStatusSchema = z.enum(TASK_UPDATE_STATUSES);

/** CO-only request: "tell me where this task stands". Targets default to current assignees. */
export const requestTaskUpdateSchema = z
  .object({
    targetUserIds: z.array(z.string().uuid()).min(1).max(20),
    notes: z.string().trim().max(500).optional(),
  })
  .strict();
export type RequestTaskUpdateRequest = z.infer<typeof requestTaskUpdateSchema>;

/** Officer (or CO) posts an update — optionally threaded to a specific request. */
export const postTaskUpdateSchema = z
  .object({
    body: z.string().trim().min(1).max(4000),
    status: taskUpdateStatusSchema.optional(),
    /** Set when responding to a specific UPDATE_REQUESTED activity. */
    parentActivityId: z.string().uuid().optional(),
  })
  .strict();
export type PostTaskUpdateRequest = z.infer<typeof postTaskUpdateSchema>;

/** A row in the per-task Updates feed (both requests and posted updates). */
export interface TaskUpdateEntryDto {
  id: string;
  taskId: string;
  type: 'UPDATE_REQUESTED' | 'UPDATE_POSTED';
  actorId: string | null;
  actorName: string | null;
  parentActivityId: string | null;
  body: string | null;
  status: TaskUpdateStatus | null;
  notes: string | null;
  targetUserIds: string[];
  createdAt: string;
}

/** Per-officer count of unresolved update requests — fuels the Overview tile. */
export interface PendingUpdatesRowDto {
  userId: string;
  name: string;
  rank: string | null;
  avatarColor: string | null;
  pending: number;
}

// -------------------- Officer Command View (Phase 7C) --------------------

/**
 * Single pending update request from THIS CO to a specific officer, shown
 * on the officer's command dossier under "Active pending requests". A row
 * exists per (requestActivity, target) pair that has no matching response.
 */
export interface OfficerDossierPendingRequestDto {
  /** TaskActivity id of the UPDATE_REQUESTED row — links into the task drawer. */
  requestId: string;
  taskId: string;
  taskTitle: string;
  boardId: string;
  boardName: string;
  requestedAt: string;
  notes: string | null;
}

/** Board the officer is OWNER or MEMBER on — drives the Boards section. */
export interface OfficerDossierBoardDto {
  boardId: string;
  name: string;
  role: 'OWNER' | 'MEMBER';
  openTaskCount: number;
  lastActivityAt: string | null;
}

/**
 * Batched response for GET /api/v1/officers/:id/dossier. CO-only. One
 * round-trip carries identity, workload, boards, and pending requests so
 * the dossier paints in a single network call.
 */
// -------------------- Task Updates Tab (Phase 7D) --------------------

/** One row in the CO's grouped Task Updates feed. */
export interface TaskUpdateFeedEntryDto {
  /** The TaskActivity id (always UPDATE_POSTED). */
  activityId: string;
  taskId: string;
  taskTitle: string;
  boardId: string;
  boardName: string;
  actor: {
    userId: string;
    name: string;
    rank: string | null;
    appointment: string | null;
    avatarColor: string | null;
    displayName: string;
  };
  body: string;
  status: TaskUpdateStatus | null;
  parentActivityId: string | null;
  createdAt: string;
  /** False = the CO has marked this update seen (notification.isRead = true). */
  isNew: boolean;
}

export interface TaskUpdateFeedResponse {
  entries: TaskUpdateFeedEntryDto[];
  /** Count of unread (isNew=true) entries — drives the sidebar badge. */
  newCount: number;
}

/**
 * Mark-seen body. Exactly ONE of the three modes — the route layer rejects
 * mixed modes with a 400 so the client can't accidentally mark too much.
 */
export type MarkUpdatesSeenRequest =
  | { activityIds: string[] }
  | { officerId: string }
  | { all: true };

export interface OfficerDossierResponse {
  user: {
    id: string;
    name: string;
    rank: string | null;
    baNumber: string | null;
    avatarColor: string | null;
    appointment: string | null;
    lastLoginAt: string | null;
  };
  workload: {
    open: number;
    overdue: number;
    done30d: number;
  };
  boards: OfficerDossierBoardDto[];
  pendingRequestsFromCo: OfficerDossierPendingRequestDto[];
}
